import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { Save, DoNotDisturbAlt, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { componentService } from '@/services';
import type { ComponentData } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { DeleteConfirmationDialog, FormContainer, WcnTextField } from '@/components';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';
import {
    validateName,
    validateIpAddress,
    validatePort,
    required,
    requiredArray,
    validateTxPowerInDbm,
    validateRxSensitivityInDbm
} from '@core/utils/validation';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import FormComboBox from '@/components/form/FormComboBox.tsx';
import { useApiStore } from '@core/store';

interface ComponentEditorProps {
    componentId: string;
    discriminator: string;
    onCancel: () => void;
    onChanged: () => void;
}

const SENSOR_CAMERA_TYPES = ['Singlesensor', 'Multisensor'];
const TX_POWER_RANGE = Array.from({ length: 21 }, (_, i) => 10 + i);
const RX_SENSITIVITY_RANGE = Array.from({ length: 81 }, (_, i) => -80 + i);

const ComponentEditor: React.FC<ComponentEditorProps> = ({ componentId, discriminator, onCancel, onChanged }) => {
    const { t } = useTranslation();
    const { notifyWarning, notifyErrorResponse, notifySuccess } = useNotifications();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [component, setComponent] = useState<ComponentData | null>(null);
    const { gatewayIds, loadGatewayIds, loadComponentIds } = useApiStore();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = useState(false);

    useEffect(() => {
        loadComponent();
        loadGatewayIds();
    }, [componentId, discriminator]);

    const loadComponent = async () => {
        setLoading(true);
        try {
            const data = await componentService.loadComponent(discriminator, componentId);

            if (data.discriminator === 'SensorCamera') {
                data.lines = Array.isArray(data.lines) ? data.lines : [];
                data.zones = Array.isArray(data.zones) ? data.zones : [];
            }

            setComponent(data);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (fieldName: string, value: any) => {
        setComponent((prev) => (prev ? { ...prev, [fieldName]: value } : null));
    };

    const handleSave = async () => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning('invalidInput');
            return;
        }

        setSaving(true);
        try {
            await componentService.updateComponent(discriminator, componentId, component);
            notifySuccess();
            await loadComponentIds();
            onChanged();
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await componentService.deleteComponent(discriminator, componentId);
            setDeleteDialogOpen(false);
            onCancel();
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setDeleting(false);
        }
    };

    const shopGatewayIds = gatewayIds.filter((g) => g.shopId === component?.shopId);

    const getIpAddressLabel = () => {
        return component?.discriminator === 'SmartShelfComponent' ? t('smartShelfBrokerIpAddress') : t('ipAddress');
    };

    const getPortLabel = () => {
        return component?.discriminator === 'SmartShelfComponent' ? t('smartShelfBrokerPort') : t('port');
    };

    if (loading || !component) {
        return (
            <Box className="editor-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="editor-container">
            {/* Header Actions */}
            <Box className="editor-sidebar-actions">
                <Button variant="text" startIcon={<Save />} onClick={handleSave} disabled={!formValid}>
                    {t('save')}
                </Button>
                <Button variant="text" startIcon={<DoNotDisturbAlt />} onClick={onCancel}>
                    {t('cancel')}
                </Button>
                <Button
                    variant="text"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => setDeleteDialogOpen(true)}
                    className="delete"
                >
                    {t('delete')}
                </Button>
            </Box>

            {/* Content */}
            <Box className="editor-sidebar-content component-editor-content">
                <Typography variant="h4" className="editor-section-header">
                    <Box component="span" className="material-icons">
                        view_list
                    </Box>
                    {t('general')}
                </Typography>

                <Box className="editor-form-container">
                    <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <WcnTextField label={t('device') + '-ID'} value={component.id} disabled fullWidth />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'name'}
                                    label={t('name')}
                                    value={component.name}
                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                    required
                                    fullWidth
                                    FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                    rules={[validateName(t)]}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <WcnTextField
                                    label={t('description')}
                                    value={component.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    fullWidth
                                />
                            </div>
                        </div>

                        {component.discriminator !== 'SnmpDevice' && (
                            <div className="form-row">
                                <div className="form-col form-col-12 form-col-6">
                                    <FormTextField
                                        name={'ipAddress'}
                                        label={getIpAddressLabel()}
                                        value={component.ipAddress}
                                        onChange={(e) => handleFieldChange('ipAddress', e.target.value)}
                                        required
                                        fullWidth
                                        FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                        rules={[validateIpAddress(t)]}
                                    />
                                </div>
                            </div>
                        )}

                        {['MediaPlayer', 'SmartShelfComponent'].includes(component.discriminator || '') && (
                            <div className="form-row">
                                <div className="form-col form-col-12 form-col-6">
                                    <FormTextField
                                        name={'port'}
                                        label={getPortLabel()}
                                        type="number"
                                        value={component.port ?? ''}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                'port',
                                                e.target.value === '' ? undefined : Number(e.target.value)
                                            )
                                        }
                                        rules={[validatePort(t)]}
                                        required
                                        fullWidth
                                        FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* SnmpDevice specific fields */}
                        {component.discriminator === 'SnmpDevice' && (
                            <div className="form-row">
                                <div className="form-col form-col-12 form-col-6">
                                    <FormSelect
                                        name={'gatewayId'}
                                        label={t('gateway')}
                                        value={component.gatewayId || ''}
                                        onChange={(e) => handleFieldChange('gatewayId', e)}
                                        items={shopGatewayIds}
                                        itemText={'deviceId'}
                                        itemValue={'id'}
                                        rules={[required(t)]}
                                    />
                                </div>
                            </div>
                        )}

                        {/* MediaPlayer specific fields */}
                        {component.discriminator === 'MediaPlayer' && (
                            <>
                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name={'resetTimeoutInMs'}
                                            label={t('resetTimeoutInMs')}
                                            type="number"
                                            value={component.resetTimeoutInMs ?? ''}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    'resetTimeoutInMs',
                                                    e.target.value === '' ? undefined : Number(e.target.value)
                                                )
                                            }
                                            rules={[required(t)]}
                                            required
                                            fullWidth
                                            FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormSelect
                                            name={'gatewayId'}
                                            label={t('gateway')}
                                            value={component.gatewayId || ''}
                                            onChange={(e) => handleFieldChange('gatewayId', e)}
                                            items={shopGatewayIds}
                                            itemText={'deviceId'}
                                            itemValue={'id'}
                                            rules={[required(t)]}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* SensorCamera specific fields */}
                        {component.discriminator === 'SensorCamera' && (
                            <>
                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormSelect
                                            name={'sensorCameraType'}
                                            label={t('type')}
                                            value={component.sensorCameraType || ''}
                                            onChange={(e) => handleFieldChange('sensorCameraType', e)}
                                            items={SENSOR_CAMERA_TYPES.map((type) => ({ key: type, value: type }))}
                                            itemText={'key'}
                                            itemValue={'value'}
                                            rules={[required(t)]}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12">
                                        <div className="field-group">
                                            <FormComboBox
                                                name={'lines'}
                                                label={t('lines')}
                                                value={component.lines || []}
                                                onChange={(vals) => handleFieldChange('lines', vals)}
                                                rules={[requiredArray(t)]}
                                                freeSolo
                                                fullWidth
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12">
                                        <div className="field-group">
                                            <FormComboBox
                                                name={'zones'}
                                                label={t('zones')}
                                                value={component.zones || []}
                                                onChange={(vals) => handleFieldChange('zones', vals)}
                                                freeSolo
                                                fullWidth
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ImpinjRfidReader specific fields */}
                        {component.discriminator === 'ImpinjRfidReader' && (
                            <>
                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormSelect
                                            name={'txPowerInDbm'}
                                            label={t('txPowerInDbm')}
                                            value={component.txPowerInDbm ?? 0}
                                            onChange={(e) =>
                                                handleFieldChange('txPowerInDbm', e === '' ? undefined : Number(e))
                                            }
                                            items={TX_POWER_RANGE.map((val) => ({ key: val, value: val }))}
                                            itemText={'key'}
                                            itemValue={'value'}
                                            rules={[validateTxPowerInDbm(TX_POWER_RANGE)(t)]}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormSelect
                                            name={'rxSensitivityInDbm'}
                                            label={t('rxSensitivityInDbm')}
                                            value={component.rxSensitivityInDbm ?? 0}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    'rxSensitivityInDbm',
                                                    e === '' ? undefined : Number(e)
                                                )
                                            }
                                            items={RX_SENSITIVITY_RANGE.map((val) => ({ key: val, value: val }))}
                                            itemText={'key'}
                                            itemValue={'value'}
                                            rules={[validateRxSensitivityInDbm(RX_SENSITIVITY_RANGE)(t)]}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <div className="field-group">
                                            <FormComboBox
                                                name={'disabledAntennas'}
                                                label={t('disabledAntennas')}
                                                value={component.disabledAntennas || []}
                                                onChange={(vals) => handleFieldChange('disabledAntennas', vals)}
                                                freeSolo
                                                fullWidth
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </FormContainer>
                </Box>
            </Box>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('deleteComponentMessage', {
                    discriminator: component.discriminator,
                    name: component.name
                })}
                onConfirm={handleDelete}
                onCancel={() => setDeleteDialogOpen(false)}
            />

            <LoadingOverlay open={saving} showMessage={true} />

            <LoadingOverlay open={deleting} showMessage={true} />
        </Box>
    );
};

export default ComponentEditor;
